import React, { useState, useEffect } from 'react'
import { 
  Save,
  RotateCcw,
  Calendar,
  User,
  MessageSquare,
  Target,
  CheckCircle,
  ChevronRight,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import autoIncrementService from '../../services/autoIncrementService'
import { followUpService } from '../../services/followUpService'
import { leadGenerationService } from '../../services/leadGenerationService'
import prospectMasterService from '../../services/prospectMasterService'
import customerMasterService from '../../services/customerMasterService'

const FollowUpForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const isView = window.location.pathname.includes('/view/')
  
  const [activeTab, setActiveTab] = useState('basicInfo')
  const [showHelp, setShowHelp] = useState(false)
  const [isLoadingFollowUpCode, setIsLoadingFollowUpCode] = useState(false)
  const [loading, setLoading] = useState(false)
  
  const [formData, setFormData] = useState({
    // Basic Information
    followUpCode: '',
    followUpDate: new Date().toISOString().split('T')[0],
    followUpTime: new Date().toTimeString().slice(0, 5),
    followUpType: 'Task',
    followUpStatus: 'Pending',
    priority: 'Medium',
    subject: '',
    followUpDescription: '',
    // Assignment Information
    assignedTo: '',
    followUpTeam: [],
    followUpMethod: 'Phone Call',
    followUpLocation: '',
    followUpDuration: 0,
    // Related Records
    relatedTo: '',
    relatedModule: '',
    lead: '',
    prospect: '',
    customer: '',
    campaign: '',
    proposal: '',
    demo: '',
    // Follow-up Details
    followUpPurpose: '',
    followUpOutcome: 'Pending',
    followUpNotes: '',
    followUpTags: [],
    followUpLabels: [],
    // Reminder and Deadline
    followUpReminder: false,
    reminderDate: '',
    reminderTime: '',
    followUpDeadline: '',
    // Completion Information
    followUpCompleted: false,
    completedDate: '',
    completedBy: '',
    completionNotes: '',
    followUpNextSteps: ''
  })

  // Master data for dropdowns
  const [leads, setLeads] = useState([])
  const [prospects, setProspects] = useState([])
  const [customers, setCustomers] = useState([])
  const [campaigns, setCampaigns] = useState([])
  const [proposals, setProposals] = useState([])
  const [demos, setDemos] = useState([])

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Home', path: '/dashboard' },
    { label: 'CRM', path: '/crm' },
    { label: 'Follow-ups', path: '/crm/follow-up' },
    { label: isEdit ? (isView ? 'View Follow-up' : 'Edit Follow-up') : 'New Follow-up' }
  ]

  // Load master data and follow-up code on component mount
  useEffect(() => {
    if (!isView) {
      loadFollowUpCode()
    }
    loadMasterData()
    if (isEdit) {
      loadFollowUpData()
    }
  }, [id, isEdit, isView])

  const loadFollowUpCode = async () => {
    try {
      setIsLoadingFollowUpCode(true)
      const response = await autoIncrementService.getCurrentValue('FOLLOW-UP')
      if (response.success) {
        const sequence = response.data.formattedValue
        setFormData(prev => ({
          ...prev,
          followUpCode: sequence
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          followUpCode: 'FU-0001'
        }))
        console.warn('Auto-increment sequence FOLLOW-UP not found, using placeholder')
      }
    } catch (error) {
      console.error('Error loading follow-up code:', error)
      setFormData(prev => ({
        ...prev,
        followUpCode: 'FU-0001'
      }))
      console.warn('Using placeholder follow-up code due to auto-increment error')
    } finally {
      setIsLoadingFollowUpCode(false)
    }
  }

  const loadMasterData = async () => {
    try {
      // Load leads
      const leadsResponse = await leadGenerationService.getAllLeads({ limit: 1000 })
      if (leadsResponse.success) {
        setLeads(leadsResponse.data.leads || [])
      }

      // Load prospects
      const prospectsResponse = await prospectMasterService.getAllProspects({ limit: 1000 })
      if (prospectsResponse.success) {
        setProspects(prospectsResponse.data.prospects || [])
      }

      // Load customers
      const customersResponse = await customerMasterService.getAllCustomers({ limit: 1000 })
      if (customersResponse.success) {
        setCustomers(customersResponse.data.customers || [])
      }

      // Set empty arrays for campaigns, proposals, demos for now
      setCampaigns([])
      setProposals([])
      setDemos([])
    } catch (error) {
      console.error('Error loading master data:', error)
      toast.error('Failed to load master data')
    }
  }

  const loadFollowUpData = async () => {
    if (!id) return
    
    try {
      setLoading(true)
      const response = await followUpService.getFollowUpById(id)
      if (response.success) {
        setFormData(response.data)
      } else {
        toast.error(response.message || 'Failed to load follow-up data')
        navigate('/crm/follow-up')
      }
    } catch (error) {
      console.error('Error loading follow-up data:', error)
      toast.error('Failed to load follow-up data')
      navigate('/crm/follow-up')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setLoading(true)
      const followUpData = {
        ...formData
      }

      let response
      if (isEdit) {
        response = await followUpService.updateFollowUp(id, followUpData)
      } else {
        response = await followUpService.createFollowUp(followUpData)
      }

      if (response.success) {
        toast.success(isEdit ? 'Follow-up updated successfully' : 'Follow-up created successfully')
        navigate('/crm/follow-up')
      } else {
        toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} follow-up`)
      }
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} follow-up:`, error)
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} follow-up`)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      followUpCode: '',
      followUpDate: new Date().toISOString().split('T')[0],
      followUpTime: new Date().toTimeString().slice(0, 5),
      followUpType: 'Task',
      followUpStatus: 'Pending',
      priority: 'Medium',
      subject: '',
      followUpDescription: '',
      assignedTo: '',
      followUpTeam: [],
      followUpMethod: 'Phone Call',
      followUpLocation: '',
      followUpDuration: 0,
      relatedTo: '',
      relatedModule: '',
      lead: '',
      prospect: '',
      customer: '',
      campaign: '',
      proposal: '',
      demo: '',
      followUpPurpose: '',
      followUpOutcome: 'Pending',
      followUpNotes: '',
      followUpTags: [],
      followUpLabels: [],
      followUpReminder: false,
      reminderDate: '',
      reminderTime: '',
      followUpDeadline: '',
      followUpCompleted: false,
      completedDate: '',
      completedBy: '',
      completionNotes: '',
      followUpNextSteps: ''
    })
    if (!isView) {
      loadFollowUpCode()
    }
  }

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="page-header">
          <div className="page-title">
            <h1>Loading...</h1>
            <p>Please wait while we load the follow-up form</p>
          </div>
        </div>
        <div className="form-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    )
  }

  const helpContent = (
    <>
      <h3>What is Follow-up Master?</h3>
      <p>Follow-up Master lets you <strong>create and track follow-up activities</strong>—tasks or activities linked to leads, prospects, customers, campaigns, proposals, or demos, with assignment, outcome, and completion.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Information</strong> – Follow-up code, date, time, type, status, priority, subject, description.</p>
      <p><strong>Assignment</strong> – Assigned to, team, method, location, duration.</p>
      <p><strong>Related Records</strong> – Link to lead, prospect, customer, campaign, proposal, demo.</p>
      <p><strong>Follow-up Details</strong> – Purpose, outcome, notes, tags, reminder, deadline.</p>
      <p><strong>Completion</strong> – Completed flag, completed date/by, completion notes, next steps.</p>
      <h3>How to use</h3>
      <p>Fill required fields. Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={breadcrumbItems}
        rightSlot={
          <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
            <HelpCircle size={18} />
            <span className="d-none d-sm-inline">Help</span>
          </button>
        }
      />
      
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title">
            <h1>{isEdit ? 'Edit Follow-up' : isView ? 'View Follow-up' : 'New Follow-up'}</h1>
            <p>{isEdit ? 'Update follow-up information and details' : isView ? 'View follow-up information and details' : 'Create a new follow-up record and track activities'}</p>
          </div>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Follow-up Master Entry</h2>
        </div>
      
      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'basicInfo' ? 'active' : ''}`}
          onClick={() => setActiveTab('basicInfo')}
        >
          <Calendar size={18} />
          <span>Basic Information</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'assignment' ? 'active' : ''}`}
          onClick={() => setActiveTab('assignment')}
        >
          <User size={18} />
          <span>Assignment</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'relatedRecords' ? 'active' : ''}`}
          onClick={() => setActiveTab('relatedRecords')}
        >
          <Target size={18} />
          <span>Related Records</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'followUpDetails' ? 'active' : ''}`}
          onClick={() => setActiveTab('followUpDetails')}
        >
          <MessageSquare size={18} />
          <span>Follow-up Details</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'completion' ? 'active' : ''}`}
          onClick={() => setActiveTab('completion')}
        >
          <CheckCircle size={18} />
          <span>Completion</span>
        </button>
      </div>
        
      <form className="form-content" onSubmit={handleSubmit}>

        {/* Basic Information Tab */}
        {activeTab === 'basicInfo' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Basic Information</h3>
            </div>
            
            {/* Row 1: Follow-up Code, Date, Time, Type */}
            <div className="grid-row-4">
              <div className="form-group">
                <label className="form-label required">Follow-up Code</label>
                <input
                  type="text"
                  value={isLoadingFollowUpCode ? 'Generating...' : formData.followUpCode}
                  readOnly
                  className="form-input"
                  placeholder="Auto-generated"
                  style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-group">
                <label className="form-label required">Follow-up Date</label>
                <input
                  type="date"
                  value={formData.followUpDate}
                  onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label required">Follow-up Time</label>
                <input
                  type="time"
                  value={formData.followUpTime}
                  onChange={(e) => handleInputChange('followUpTime', e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label required">Follow-up Type</label>
                <select
                  value={formData.followUpType}
                  onChange={(e) => handleInputChange('followUpType', e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="Task">Task</option>
                  <option value="Call">Call</option>
                  <option value="Email">Email</option>
                  <option value="Meeting">Meeting</option>
                  <option value="Demo">Demo</option>
                  <option value="Proposal">Proposal</option>
                  <option value="Follow-up">Follow-up</option>
                </select>
              </div>
            </div>

            {/* Row 2: Status, Priority, Subject */}
            <div className="grid-row-3">
              <div className="form-group">
                <label className="form-label required">Status</label>
                <select
                  value={formData.followUpStatus}
                  onChange={(e) => handleInputChange('followUpStatus', e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="On Hold">On Hold</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label required">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label required">Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  className="form-input"
                  placeholder="Enter follow-up subject"
                  required
                />
              </div>
            </div>

            {/* Row 3: Description */}
            <div className="grid-row-1">
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  value={formData.followUpDescription}
                  onChange={(e) => handleInputChange('followUpDescription', e.target.value)}
                  className="form-input"
                  rows="4"
                  placeholder="Enter detailed follow-up description"
                />
              </div>
            </div>
          </div>
        )}

        {/* Assignment Tab */}
        {activeTab === 'assignment' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Assignment Information</h3>
            </div>
            
            {/* Row 1: Assigned To, Method, Location */}
            <div className="grid-row-3">
              <div className="form-group">
                <label className="form-label">Assigned To</label>
                <input
                  type="text"
                  value={formData.assignedTo}
                  onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                  className="form-input"
                  placeholder="Enter assigned person"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Follow-up Method</label>
                <select
                  value={formData.followUpMethod}
                  onChange={(e) => handleInputChange('followUpMethod', e.target.value)}
                  className="form-input"
                >
                  <option value="Phone Call">Phone Call</option>
                  <option value="Email">Email</option>
                  <option value="Meeting">Meeting</option>
                  <option value="Video Call">Video Call</option>
                  <option value="SMS">SMS</option>
                  <option value="In-Person">In-Person</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Location</label>
                <input
                  type="text"
                  value={formData.followUpLocation}
                  onChange={(e) => handleInputChange('followUpLocation', e.target.value)}
                  className="form-input"
                  placeholder="Enter location"
                />
              </div>
            </div>

            {/* Row 2: Duration, Reminder, Deadline */}
            <div className="grid-row-3">
              <div className="form-group">
                <label className="form-label">Duration (minutes)</label>
                <input
                  type="number"
                  value={formData.followUpDuration}
                  onChange={(e) => handleInputChange('followUpDuration', parseInt(e.target.value) || 0)}
                  className="form-input"
                  placeholder="Enter duration in minutes"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Reminder Date</label>
                <input
                  type="date"
                  value={formData.reminderDate}
                  onChange={(e) => handleInputChange('reminderDate', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Deadline</label>
                <input
                  type="date"
                  value={formData.followUpDeadline}
                  onChange={(e) => handleInputChange('followUpDeadline', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Related Records Tab */}
        {activeTab === 'relatedRecords' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Related Records</h3>
            </div>
            
            {/* Row 1: Lead, Prospect */}
            <div className="grid-row-2">
              <div className="form-group">
                <label className="form-label">Lead</label>
                <select
                  value={formData.lead}
                  onChange={(e) => handleInputChange('lead', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Lead</option>
                  {leads.map(lead => (
                    <option key={lead._id} value={lead._id}>
                      {lead.leadCode} - {lead.prospect?.prospectName || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Prospect</label>
                <select
                  value={formData.prospect}
                  onChange={(e) => handleInputChange('prospect', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Prospect</option>
                  {prospects.map(prospect => (
                    <option key={prospect._id} value={prospect._id}>
                      {prospect.prospectCode} - {prospect.prospectName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 2: Customer, Campaign */}
            <div className="grid-row-2">
              <div className="form-group">
                <label className="form-label">Customer</label>
                <select
                  value={formData.customer}
                  onChange={(e) => handleInputChange('customer', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option key={customer._id} value={customer._id}>
                      {customer.customerCode} - {customer.customerName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Campaign</label>
                <select
                  value={formData.campaign}
                  onChange={(e) => handleInputChange('campaign', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Campaign</option>
                  {campaigns.map(campaign => (
                    <option key={campaign._id} value={campaign._id}>
                      {campaign.campaignCode} - {campaign.campaignName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3: Proposal, Demo */}
            <div className="grid-row-2">
              <div className="form-group">
                <label className="form-label">Proposal</label>
                <select
                  value={formData.proposal}
                  onChange={(e) => handleInputChange('proposal', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Proposal</option>
                  {proposals.map(proposal => (
                    <option key={proposal._id} value={proposal._id}>
                      {proposal.proposalCode} - {proposal.proposalTitle}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Demo</label>
                <select
                  value={formData.demo}
                  onChange={(e) => handleInputChange('demo', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Demo</option>
                  {demos.map(demo => (
                    <option key={demo._id} value={demo._id}>
                      {demo.demoCode} - {demo.demoTitle}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Follow-up Details Tab */}
        {activeTab === 'followUpDetails' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Follow-up Details</h3>
            </div>
            
            {/* Row 1: Purpose, Outcome */}
            <div className="grid-row-2">
              <div className="form-group">
                <label className="form-label">Purpose</label>
                <select
                  value={formData.followUpPurpose}
                  onChange={(e) => handleInputChange('followUpPurpose', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Purpose</option>
                  <option value="Sales">Sales</option>
                  <option value="Support">Support</option>
                  <option value="Follow-up">Follow-up</option>
                  <option value="Information">Information</option>
                  <option value="Demo">Demo</option>
                  <option value="Negotiation">Negotiation</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Outcome</label>
                <select
                  value={formData.followUpOutcome}
                  onChange={(e) => handleInputChange('followUpOutcome', e.target.value)}
                  className="form-input"
                >
                  <option value="Pending">Pending</option>
                  <option value="Successful">Successful</option>
                  <option value="Partially Successful">Partially Successful</option>
                  <option value="Unsuccessful">Unsuccessful</option>
                  <option value="No Response">No Response</option>
                  <option value="Rescheduled">Rescheduled</option>
                </select>
              </div>
            </div>

            {/* Row 2: Notes */}
            <div className="grid-row-1">
              <div className="form-group">
                <label className="form-label">Follow-up Notes</label>
                <textarea
                  value={formData.followUpNotes}
                  onChange={(e) => handleInputChange('followUpNotes', e.target.value)}
                  className="form-input"
                  rows="4"
                  placeholder="Enter follow-up notes and observations"
                />
              </div>
            </div>
          </div>
        )}

        {/* Completion Tab */}
        {activeTab === 'completion' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Completion Information</h3>
            </div>
            
            {/* Row 1: Completed, Completed Date, Completed By */}
            <div className="grid-row-3">
              <div className="form-group">
                <label className="form-label">Completed</label>
                <select
                  value={formData.followUpCompleted}
                  onChange={(e) => handleInputChange('followUpCompleted', e.target.value === 'true')}
                  className="form-input"
                >
                  <option value={false}>No</option>
                  <option value={true}>Yes</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Completed Date</label>
                <input
                  type="date"
                  value={formData.completedDate}
                  onChange={(e) => handleInputChange('completedDate', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Completed By</label>
                <input
                  type="text"
                  value={formData.completedBy}
                  onChange={(e) => handleInputChange('completedBy', e.target.value)}
                  className="form-input"
                  placeholder="Enter completed by"
                />
              </div>
            </div>

            {/* Row 2: Completion Notes */}
            <div className="grid-row-1">
              <div className="form-group">
                <label className="form-label">Completion Notes</label>
                <textarea
                  value={formData.completionNotes}
                  onChange={(e) => handleInputChange('completionNotes', e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Enter completion notes and summary"
                />
              </div>
            </div>

            {/* Row 3: Next Steps */}
            <div className="grid-row-1">
              <div className="form-group">
                <label className="form-label">Next Steps</label>
                <textarea
                  value={formData.followUpNextSteps}
                  onChange={(e) => handleInputChange('followUpNextSteps', e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Enter next steps and action items"
                />
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleReset}
          >
            <RotateCcw size={18} />
            Reset
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            <Save size={18} />
            {loading ? 'Saving...' : (isEdit ? 'Update Follow-up' : 'Create Follow-up')}
          </button>
        </div>
      </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Follow-up Master Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default FollowUpForm